import { useRouter } from 'next/router'
import { PageNames } from '../integrations/amplitude/types'
import { IdentityErrorCode } from '@afterpaytouch/portal-api/identity'

// TODO: [PAYL-18020] CP: Better route enum
export enum Route {
  INDEX = '/',
  LOGIN_PASSWORD = '/password',
  CREATE_PASSWORD_VERIFY = '/create-password-verify',
  CREATE_PASSWORD = '/create-password',
  HOME = '/home',
  INDEX__PAYNOW_ADDBANKACCOUNTMODAL = '/?modal-pay-now=true&modal-add-bank-account=true',
  UPCOMING_PAYMENTS = '/upcoming-payments',
  UPCOMING_PAYMENTS__PAYNOW_ADDBANKACCOUNTMODAL = '/upcoming-payments?modal-pay-now=true&modal-add-bank-account=true',
  ORDERS = '/orders',
  ORDER = '/orders/[id]',
  ORDER_BY_TRANSACTION_TOKEN = '/orders/transaction/[token]',
  ORDER__PAYNOW_ADDBANKACCOUNTMODAL = '/orders/[id]?modal-pay-now=true&modal-add-bank-account=true',
  ORDER__CHANGEPAYMENTMETHOD_ADDBANKACCOUNTMODAL = '/orders/[id]?modal-change-payment-method=true&modal-change-payment-add-bank-account=true',
  ACCOUNT = '/account',
  ACCOUNT__PROFILE = '/account?tab-acc=profile',
  ACCOUNT__PROFILE__CLOSEORREACTIVATEACCOUNTMODAL = '/account?tab-acc=profile&modal-cora=true',
  ACCOUNT__PULSE = '/account?tab-acc=pulse',
  ACCOUNT__PAYMENT_METHODS = '/account?tab-acc=payment-methods',
  ACCOUNT__PAYMENT_SETTINGS = '/account?tab-acc=payment-settings',
  ACCOUNT__PAYMENT_METHODS__ADDBANKACCOUNTMODAL = '/account?tab-acc=payment-methods&modal-add-bank-account=true',
  ACCOUNT__PREFERENCES = '/account?tab-acc=preferences',
  ACCOUNT__SECURITY = '/account?tab-acc=security',
  PPA = '/ppa/[documentId]',
  RECURRING_PAYMENTS = '/recurring-payments',
  PLAID_FINISHED = '/plaid-finished',
  RECURRING_PAYMENTS__CHANGEPAYMENTMETHOD_ADDBANKACCOUNTMODAL = '/recurring-payments?modal-change-payment-method=true&modal-add-bank-account=true',
  UNSUBSCRIBE = '/unsubscribe',
  VERIFY_IDENTITY = '/verify-identity',
  VERIFY_LICENCE = '/verify-identity/licence',
  VERIFY_MEDICARE = '/verify-identity/medicare',
  VERIFY_PASSPORT = '/verify-identity/passport',
  VERIFY_ADDRESS = '/verify-identity/address',
  RETRY_VERIFY_LICENCE = '/verify-identity/retry/licence',
  RETRY_VERIFY_PASSPORT = '/verify-identity/retry/passport',
  RETRY_VERIFY_MEDICARE = '/verify-identity/retry/medicare',
  VERIFY_RETRY = '/verify-retry',
  TERMINAL_ERROR = '/error',
  TERMINAL_ERROR_DEFAULT = '/error/default',
  PASSWORD_RESET_REQUIRED = '/password-reset-required',
  FORCE_PASSWORD_RESET = '/force-password-reset',
  PROTECT_ACCOUNT = '/order-email-survey/account-protect',
  CONFIRM_ORDER = '/order-email-survey/order-confirm',
  ACCOUNT__PROFILE__REACTIVATEACCOUNTMODAL = '/account?tab-acc=profile&modal-reactivate-account=true',
  PAY_MONTHLY = '/pay-monthly',
  PAY_MONTHLY_DECREASE_LIMIT = '/pay-monthly/decrease-limit',
  VERIFY_CHALLENGE = '/verify-challenge',
  VERIFY_2FA = '/verify-2fa',
  PROFILE_CONFIRM = '/profile-confirm',
  UNSUITABILITY_QUESTION_FORM = '/unsuitability-questions/form',
  AFFORDABILITY_QUESTION_FORM = '/affordability-questions/form',
  CONTRACT_AGREEMENT = '/contract-agreement',
  LOGGED_IN = '/logged-in',
  SIGNUP = '/signup',
  FINANCIAL_SUPPORT = '/financial-support',
  FINANCIAL_SUPPORT_PAYMENT_ARRANGEMENT = '/financial-support/payment-arrangement',
  // TODO add these page IDENTITY-3221
  VERIFYING_BIOMETRIC = '/verify-identity/biometric/verifying',
  VERIFY_BIOMETRIC_PERSONA = '/verify-identity/biometric/persona',
  VERIFY_BIOMETRIC_LICENCE = '/verify-identity/biometric/licence',
  VERIFY_BIOMETRIC_PASSPORT = '/verify-identity/biometric/passport',
  VERIFY_IDENTITY_START = '/verify-identity-start',
  DOCUMENT = '/verify-identity/[document]',
  DOCUMENT_RETRY = '/verify-identity/[document]/retry',
  ADD_CASH_PAYMENT_METHOD = '/cash',
  SHADOW_ACCOUNT_MOBILE_LOGIN = '/cash-login',
  CASH_WEB_LOGIN_CALLBACK = '/cash-callback',
  VERIFY_SMS = '/verify-sms',
  PROFILE_CREATED = '/profile-created',
  MANUAL_IDV = '/verify-identity/upload-documents',
}

const UnauthenticatedRoutes: { [key: string]: Route } = {
  Unsubscribe: Route.UNSUBSCRIBE,
  PlaidFinished: Route.PLAID_FINISHED,
  PasswordResetRequired: Route.PASSWORD_RESET_REQUIRED,
  ForcePasswordReset: Route.FORCE_PASSWORD_RESET,
  ProtectAccount: Route.PROTECT_ACCOUNT,
  ConfirmOrder: Route.CONFIRM_ORDER,
  Index: Route.INDEX,
  VerifyChallenge: Route.VERIFY_CHALLENGE,
  Verify2FA: Route.VERIFY_2FA,
  LoginPassword: Route.LOGIN_PASSWORD,
  CreatePasswordVerify: Route.CREATE_PASSWORD_VERIFY,
  CreatePassword: Route.CREATE_PASSWORD,
  LoggedIn: Route.LOGGED_IN,
  Signup: Route.SIGNUP,
  ShadowAccountMobileLogin: Route.SHADOW_ACCOUNT_MOBILE_LOGIN,
  CashWebLoginCallback: Route.CASH_WEB_LOGIN_CALLBACK,
  VERIFY_SMS: Route.VERIFY_SMS,
  ProfileCreated: Route.PROFILE_CREATED,
} as const
const LoginFlowRoutes: { [key: string]: Route } = {
  PasswordResetRequired: Route.PASSWORD_RESET_REQUIRED,
  ForcePasswordReset: Route.FORCE_PASSWORD_RESET,
  VerifyChallenge: Route.VERIFY_CHALLENGE,
  LoginPassword: Route.LOGIN_PASSWORD,
  CreatePasswordVerify: Route.CREATE_PASSWORD_VERIFY,
  CreatePassword: Route.CREATE_PASSWORD,
} as const
export type UnauthenticatedRoute = (typeof UnauthenticatedRoutes)[keyof typeof UnauthenticatedRoutes]
export const UnauthenticatedRoutesList: UnauthenticatedRoute[] = Object.values(UnauthenticatedRoutes)
export const LoginFlowRoutesList: UnauthenticatedRoute[] = Object.values(LoginFlowRoutes)

export const routePageNames: Partial<Record<Route, PageNames>> = {
  [Route.INDEX]: PageNames.INDEX,
  [Route.LOGIN_PASSWORD]: PageNames.LOGIN_PASSWORD,
  [Route.CREATE_PASSWORD_VERIFY]: PageNames.CREATE_PASSWORD_VERIFY,
  [Route.CREATE_PASSWORD]: PageNames.CREATE_PASSWORD,
  [Route.PROFILE_CONFIRM]: PageNames.PROFILE_CONFIRM,
  [Route.HOME]: PageNames.HOME,
  [Route.UPCOMING_PAYMENTS]: PageNames.UPCOMING_PAYMENTS,
  [Route.ORDERS]: PageNames.ORDERS,
  [Route.ORDER]: PageNames.ORDER,
  [Route.ACCOUNT]: PageNames.ACCOUNT,
  [Route.PPA]: PageNames.PPA,
  [Route.RECURRING_PAYMENTS]: PageNames.RECURRING_PAYMENTS,
  [Route.UNSUBSCRIBE]: PageNames.UNSUBSCRIBE,
  [Route.VERIFY_IDENTITY]: PageNames.VERIFY_IDENTITY,
  [Route.VERIFY_LICENCE]: PageNames.VERIFY_LICENCE,
  [Route.VERIFY_MEDICARE]: PageNames.VERIFY_MEDICARE,
  [Route.VERIFY_PASSPORT]: PageNames.VERIFY_PASSPORT,
  [Route.RETRY_VERIFY_LICENCE]: PageNames.RETRY_VERIFY_HARD_ID,
  [Route.RETRY_VERIFY_PASSPORT]: PageNames.RETRY_VERIFY_HARD_ID,
  [Route.RETRY_VERIFY_MEDICARE]: PageNames.RETRY_VERIFY_HARD_ID,
  [Route.VERIFY_ADDRESS]: PageNames.SIGNUP_RETRY,
  [Route.VERIFY_RETRY]: PageNames.VERIFY_RETRY,
  [Route.FORCE_PASSWORD_RESET]: PageNames.FORCE_PASSWORD_RESET,
  [Route.PROTECT_ACCOUNT]: PageNames.PROTECT_ACCOUNT,
  [Route.CONFIRM_ORDER]: PageNames.CONFIRM_ORDER,
  [Route.PAY_MONTHLY]: PageNames.PAY_MONTHLY,
  [Route.PAY_MONTHLY_DECREASE_LIMIT]: PageNames.PAY_MONTHLY_DECREASE_LIMIT,
  [Route.TERMINAL_ERROR]: PageNames.TERMINAL_ERROR,
  [Route.VERIFY_2FA]: PageNames.VERIFY_2FA,
  [Route.VERIFY_CHALLENGE]: PageNames.VERIFY_CHALLENGE,
  [Route.UNSUITABILITY_QUESTION_FORM]: PageNames.UNSUITABILITY_QUESTIONS,
  [Route.AFFORDABILITY_QUESTION_FORM]: PageNames.AFFORDABILITY_QUESTIONS,
  [Route.CONTRACT_AGREEMENT]: PageNames.CONTRACT_AGREEMENT,
  [Route.PLAID_FINISHED]: PageNames.PLAID_FINISHED,
  [Route.VERIFY_IDENTITY_START]: PageNames.VERIFY_IDENTITY_START,
  [Route.VERIFYING_BIOMETRIC]: PageNames.VERIFYING_BIOMETRIC,
  [Route.VERIFY_BIOMETRIC_PERSONA]: PageNames.VERIFY_BIOMETRIC_PERSONA,
  [Route.VERIFY_BIOMETRIC_LICENCE]: PageNames.VERIFY_BIOMETRIC_LICENCE,
  [Route.VERIFY_BIOMETRIC_PASSPORT]: PageNames.VERIFY_BIOMETRIC_PASSPORT,
  [Route.LOGGED_IN]: PageNames.LOGGED_IN,
  [Route.SIGNUP]: PageNames.SIGNUP,
  [Route.DOCUMENT]: PageNames.DOCUMENT,
  [Route.DOCUMENT_RETRY]: PageNames.DOCUMENT_RETRY,
  [Route.FINANCIAL_SUPPORT]: PageNames.FINANCIAL_SUPPORT,
  [Route.FINANCIAL_SUPPORT_PAYMENT_ARRANGEMENT]: PageNames.FINANCIAL_SUPPORT_PAYMENT_ARRANGEMENT,
  [Route.PROFILE_CREATED]: PageNames.PROFILE_CREATED,
  [Route.MANUAL_IDV]: PageNames.MANUAL_IDV,
}

export const useRoutePageName = (): PageNames => {
  const { pathname } = useRouter()
  const normalizedPathname = pathname.startsWith('/error/') ? '/error' : pathname
  return routePageNames[normalizedPathname]
}

export const useIsUnauthenticatedRoute = (): boolean => {
  const route = useRouter().route as Route
  if (route === ('/error/[errorCode]' as string)) {
    return true
  }
  return UnauthenticatedRoutesList.includes(route)
}
export const useIsLoginFlowRoute = (): boolean => {
  return LoginFlowRoutesList.includes(useRouter().route as Route)
}

export const useIsAccountPage = (): boolean => {
  const router = useRouter()
  return router?.pathname === '/account'
}

export const terminalErrorCodes = [IdentityErrorCode.UnsuitabilityDecline, IdentityErrorCode.CreditCheckFailure, IdentityErrorCode.AffordabilityDecline]
export const isTerminalErrorCode = (errorCode: IdentityErrorCode): boolean => {
  return terminalErrorCodes.includes(errorCode)
}
